import type { RosterEntry } from '@veriverse/shared';

export interface ColleagueRow {
  readonly userId: string;
  readonly displayName: string;
  readonly lastActiveAt: string | null;
}

export interface ColleagueGroups {
  readonly online: ColleagueRow[];
  readonly offline: ColleagueRow[];
}

const byName = (a: ColleagueRow, b: ColleagueRow) =>
  a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });

/** Online first by name; offline most recently active first (never seen last). */
export function groupColleagues(
  roster: readonly RosterEntry[],
  onlineIds: ReadonlySet<string>,
): ColleagueGroups {
  const online: ColleagueRow[] = [];
  const offline: ColleagueRow[] = [];
  for (const entry of roster) (onlineIds.has(entry.userId) ? online : offline).push(entry);
  const time = (row: ColleagueRow) => (row.lastActiveAt ? Date.parse(row.lastActiveAt) : -Infinity);
  return {
    online: online.sort(byName),
    offline: offline.sort((a, b) => time(b) - time(a) || byName(a, b)),
  };
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export type LastActive =
  | { readonly key: 'never' | 'justNow' }
  | { readonly key: 'minutes' | 'hours' | 'days'; readonly count: number }
  | { readonly key: 'yesterday'; readonly time: string }
  | { readonly key: 'date'; readonly date: string };

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** "5m ago", "3h ago", "yesterday at 4:15 PM", "3d ago", then an exact date after a week. */
export function describeLastActive(
  lastActiveAt: string | null,
  now: Date,
  locale: string,
): LastActive {
  if (!lastActiveAt) return { key: 'never' };
  const then = new Date(lastActiveAt);
  const age = Math.max(0, now.getTime() - then.getTime());
  if (age < MINUTE) return { key: 'justNow' };
  if (age < HOUR) return { key: 'minutes', count: Math.floor(age / MINUTE) };
  if (age < DAY && startOfDay(then) === startOfDay(now)) {
    return { key: 'hours', count: Math.floor(age / HOUR) };
  }
  const tag = locale === 'sv' ? 'sv-SE' : 'en-GB';
  const days = Math.round((startOfDay(now) - startOfDay(then)) / DAY);
  if (days === 1) {
    const time = new Intl.DateTimeFormat(tag, { hour: 'numeric', minute: '2-digit' }).format(then);
    return { key: 'yesterday', time };
  }
  if (age < DAY) return { key: 'hours', count: Math.floor(age / HOUR) };
  if (days < 7) return { key: 'days', count: days };
  const date = new Intl.DateTimeFormat(tag, {
    day: 'numeric',
    month: 'short',
    ...(then.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(then);
  return { key: 'date', date };
}
